import type { User, UserStatus } from '@/api/auth';
import { CMSGroupMode, type CMSGroupParams, EWeekday } from '@/api/cms/base';
import { timestampByUTC } from './dateTime';

export const schedule = <
  T extends Pick<CMSGroupParams, 'durationFrom' | 'durationTo'>,
>({
  durationFrom,
  durationTo,
}: T) => {
  if (durationFrom == null && durationTo == null) return true;

  const currentTimestamp = timestampByUTC(new Date().toUTCString());

  if (durationFrom == null && durationTo != null) {
    const durationToTimestamp = timestampByUTC(durationTo);
    return currentTimestamp < durationToTimestamp;
  }

  if (durationFrom != null && durationTo == null) {
    const durationFromTimestamp = timestampByUTC(durationFrom);

    return durationFromTimestamp < currentTimestamp;
  }

  if (durationFrom != null && durationTo != null) {
    const durationFromTimestamp = timestampByUTC(durationFrom);
    const durationToTimestamp = timestampByUTC(durationTo);
    return (
      durationFromTimestamp < currentTimestamp &&
      currentTimestamp < durationToTimestamp
    );
  }

  return true;
};

const filterWithMode = <T>(
  array: T[],
  mode: CMSGroupMode,
  fn: (value: T) => unknown
): boolean =>
  ({
    [CMSGroupMode.OR]: array.some(fn),
    [CMSGroupMode.AND]: array.every(fn),
  })[mode];

export const findGroup = (userGroups: UserStatus[]) => (group: string) =>
  userGroups.findIndex(({ id }) => id === group) !== -1;

export const filterByGroup = (
  groups: string[],
  mode: CMSGroupMode,
  userGroups?: UserStatus[]
) => filterWithMode(groups, mode, findGroup(userGroups || []));

export const parseGroup = (groupString: string | null) => {
  if (groupString == null || groupString === '') return [];
  return groupString.replace(/\s/g, '').split(',');
};

export const groupsInclude =
  (user?: User) =>
  <T extends CMSGroupParams>({
    groupsIncludeMode,
    groupsInclude: includeGroups,
  }: T) => {
    const groups = parseGroup(includeGroups);

    if (groups.length === 0) return true;

    if (!user?.statuses) return false;

    return filterByGroup(groups, groupsIncludeMode, user.statuses);
  };

export const groupsExclude =
  (user?: User) =>
  <T extends CMSGroupParams>({
    groupsExcludeMode,
    groupsExclude: excludeGroups,
  }: T) => {
    const groups = parseGroup(excludeGroups);

    if (groups.length === 0) return true;

    if (!user?.statuses) return false;

    return !filterByGroup(groups, groupsExcludeMode, user.statuses);
  };

type FilterByGroupAndDurationParams = {
  user?: User;
};

export const filterByGroupAndDuration =
  <T extends CMSGroupParams>({ user }: FilterByGroupAndDurationParams) =>
  (items: T[]): T[] =>
    items.filter(
      (item) =>
        schedule(item) && groupsInclude(user)(item) && groupsExclude(user)(item)
    );

export const weekdays: EWeekday[] = [
  EWeekday.Sunday,
  EWeekday.Monday,
  EWeekday.Tuesday,
  EWeekday.Wednesday,
  EWeekday.Thursday,
  EWeekday.Friday,
  EWeekday.Saturday,
];

export const replacePlaceholder = (
  str: string,
  placeholder: string,
  replacement: string
) => str.replace(`{{${placeholder}}}`, replacement);
